import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/public/SEO";
import { Check, Quote } from "lucide-react";

export default function CapellaStoriesPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", title: "", message: "", location_context: "", consent: true });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const load = async () => { const { data } = await api.get("/stories?page_size=12"); setItems(data.items || []); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/stories/submit", form);
      setState({ loading: false, ok: true, error: "" });
      setForm({ name: "", email: "", title: "", message: "", location_context: "", consent: true });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <>
      <SEO title="Capella Stories — Kapadokya'da Yaşanan Hikâyeler"
           description="Kapadokya'ya gelenlerin gerçek hikâyeleri. Bal ayları, evlilik teklifleri, dönüşler, ilk seyahatler — sizin sözleriniz." />

      <section className="pt-40 pb-12 max-w-5xl mx-auto px-6 md:px-12 text-center" data-testid="stories-header">
        <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-5">Capella Stories</div>
        <h1 className="font-serif text-5xl md:text-7xl text-cc-text leading-[1.02] mb-8">
          Kapadokya'da<br/><span className="italic">yaşanmış hikâyeler.</span>
        </h1>
        <p className="max-w-2xl mx-auto text-cc-muted text-lg leading-[1.85]">
          İnsan hikâyeleri, hiçbir rehberin anlatamadığı kadar gerçektir. Kapadokya'ya gelen,
          gelecek olan herkesin sözünü dinleyelim. Belki sizinki de buradadır.
        </p>
      </section>

      {/* Stories grid */}
      <section className="py-12 md:py-20 max-w-6xl mx-auto px-6 md:px-12" data-testid="stories-list">
        {items.length === 0 ? <p className="text-cc-muted text-center">Henüz paylaşılmış hikâye yok. İlk hikâyeyi siz yazın.</p> :
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {items.map((s, i) => (
              <article key={s.id} className="bg-white border border-cc-border p-8 md:p-10 cc-reveal" style={{ animationDelay: `${0.05 * i}s` }} data-testid={`story-${s.id}`}>
                <Quote size={20} className="text-cc-sepia mb-4" />
                <h3 className="font-serif text-2xl text-cc-text mb-4 leading-snug">{s.title}</h3>
                <p className="text-cc-muted leading-[1.85] mb-6 text-[0.95rem]">{s.message}</p>
                <div className="text-[10px] tracking-[0.22em] uppercase text-cc-sepia">— {s.name}</div>
              </article>
            ))}
          </div>}
      </section>

      {/* Submit form */}
      <section className="py-24 md:py-32 bg-cc-dark/[0.03] border-y border-cc-border">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {state.ok ? (
            <div className="text-center py-16" data-testid="story-ok">
              <Check size={36} className="mx-auto text-cc-sepia mb-5" />
              <h3 className="font-serif text-3xl mb-3" data-testid="story-submit-success">Hikâyeniz alındı.</h3>
              <p className="text-cc-muted">Editörlerimiz inceledikten sonra yayınlanacak. Teşekkürler.</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4">Hikâyenizi Paylaşın</div>
                <h2 className="font-serif text-3xl md:text-5xl text-cc-text">Sizin de bir<br/><span className="italic">Kapadokya anınız var.</span></h2>
              </div>
              <form onSubmit={submit} className="space-y-6 bg-white border border-cc-border p-8 md:p-10" data-testid="story-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div><label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Ad Soyad</label>
                    <input data-testid="story-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cc-input" /></div>
                  <div><label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">E-posta</label>
                    <input data-testid="story-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cc-input" /></div>
                </div>
                <div><label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Başlık</label>
                  <input data-testid="story-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="cc-input" placeholder="Örn: Balayımızı burada geçirdik" /></div>
                <div><label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Hikâyeniz</label>
                  <textarea data-testid="story-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="cc-input min-h-[160px]" placeholder="Yaşadığınız anı paylaşın..." /></div>
                <div><label className="text-[10px] tracking-[0.22em] uppercase text-cc-muted">Yer (opsiyonel)</label>
                  <input data-testid="story-location" value={form.location_context} onChange={(e) => setForm({ ...form, location_context: e.target.value })} className="cc-input" placeholder="Göreme, Uçhisar, Aşk Vadisi..." /></div>
                <label className="flex items-start gap-3 text-xs text-cc-muted">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
                  <span>Hikâyemin Capella Cave sayfalarında yayınlanmasını onaylıyorum. İsmim ve hikâyem dışında kişisel bilgilerim paylaşılmaz.</span>
                </label>
                <button type="submit" disabled={state.loading || !form.consent} className="cc-btn w-full justify-center" data-testid="story-submit">
                  {state.loading ? "Gönderiliyor…" : "Hikâyeni Gönder"}
                </button>
                {state.error && <p className="text-sm text-red-600">{state.error}</p>}
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
}
